// src/pages/admin/AdminLabRulesPage.jsx — Pro v2 uniforme
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Button, IconButton, Stack, Snackbar, Alert,
  TextField, Switch, Chip, Tooltip, LinearProgress, CircularProgress,
  Paper, InputAdornment, FormControl, Select, MenuItem,
} from "@mui/material";
import SaveIcon              from "@mui/icons-material/Save";
import RefreshIcon           from "@mui/icons-material/Refresh";
import SearchIcon            from "@mui/icons-material/Search";
import CheckIcon             from "@mui/icons-material/Check";
import RuleIcon              from "@mui/icons-material/Rule";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import api, { getUserMessage } from "../../api/axios";
import { pageWrap, tableHeaderCell, tableRow, C } from "../../utils/pageStyles";

const r2 = { borderRadius: 2 };

// ── Champ numérique inline ────────────────────────────────────────────────────
// Reprend exactement le style input des autres pages (border divider, r2, focus accent)
const NumInput = ({ value, onChange, accent = "#6366f1" }) => (
  <TextField
    type="number"
    size="small"
    value={value ?? ""}
    onChange={onChange}
    placeholder="∞"
    inputProps={{
      min: 0,
      style: { textAlign: "center", fontSize: 13, fontWeight: 600, padding: "5px 8px" },
    }}
    sx={{
      width: 86,
      "& .MuiOutlinedInput-root": {
        ...r2,
        "& fieldset": { borderColor: "#e2e8f0" },
        "&:hover fieldset": { borderColor: accent + "88" },
        "&.Mui-focused fieldset": { borderColor: accent },
      },
      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" },
      "& input[type=number]": { MozAppearance: "textfield" },
    }}
  />
);

const numOrNull = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

export default function AdminLabRulesPage() {
  const { t } = useTranslation();

  const [labs,     setLabs]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("all");
  const [snack,    setSnack]    = useState(null);

  const notify = (text, sev = "success") => setSnack({ text, sev });

  const loadLabs = async () => {
    setLoading(true);
    try {
      const res = await api.get("/labs");
      const d = res.data;
      setLabs(Array.isArray(d) ? d : (d?.items || d?.results || d?.data || []));
    } catch (e) {
      notify(getUserMessage(e) || t("adminLabRules.saveError"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLabs(); }, []);

  const setField = (labId, field, raw) => {
    setLabs(prev => prev.map(l => {
      if (l.id !== labId) return l;
      if (field === "is_active") return { ...l, is_active: !!raw };
      const nums = [
        "capacity", "max_daily_minutes_per_user",
        "max_weekly_minutes_per_user", "max_active_reservations_per_user",
      ];
      if (nums.includes(field)) return { ...l, [field]: raw === "" ? null : Number(raw) };
      return { ...l, [field]: raw };
    }));
  };

  const hasQuota = (l) =>
    (l.max_daily_minutes_per_user         || 0) > 0 ||
    (l.max_weekly_minutes_per_user        || 0) > 0 ||
    (l.max_active_reservations_per_user   || 0) > 0;

  const handleSave = async (lab) => {
    setSavingId(lab.id);
    try {
      await api.put(`/labs/${lab.id}`, {
        name:                             lab.name,
        description:                      lab.description,
        topology_file:                    lab.topology_file,
        is_active:                        lab.is_active,
        template_project_id:              lab.template_project_id || null,
        capacity:                         numOrNull(lab.capacity),
        max_daily_minutes_per_user:       numOrNull(lab.max_daily_minutes_per_user),
        max_weekly_minutes_per_user:      numOrNull(lab.max_weekly_minutes_per_user),
        max_active_reservations_per_user: numOrNull(lab.max_active_reservations_per_user),
      });
      setSavedIds(s => new Set([...s, lab.id]));
      setTimeout(() => setSavedIds(s => { const n = new Set(s); n.delete(lab.id); return n; }), 2200);
      notify(t("adminLabRules.saveSuccess"));
    } catch (e) {
      notify(getUserMessage(e) || t("adminLabRules.saveError"), "error");
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    let res = labs;
    if (filter === "active")     res = res.filter(l =>  l.is_active);
    if (filter === "inactive")   res = res.filter(l => !l.is_active);
    if (filter === "with_quota") res = res.filter(hasQuota);
    const q = search.trim().toLowerCase();
    if (q) res = res.filter(l =>
      (l.name || "").toLowerCase().includes(q) ||
      (l.description || "").toLowerCase().includes(q)
    );
    return res;
  }, [labs, filter, search]);

  const activeCount = labs.filter(l =>  l.is_active).length;
  const quotaCount  = labs.filter(hasQuota).length;

  const HEADERS = [
    { label: t("adminLabRules.columns.labName"),                  align: "left"   },
    { label: t("adminLabRules.columns.templateProjectId"),        align: "left"   },
    { label: t("adminLabRules.columns.active"),                   align: "center" },
    { label: t("adminLabRules.columns.capacity"),                 align: "center", accent: "#0ea5e9" },
    { label: t("adminLabRules.columns.dailyMinutesPerUser"),      align: "center", accent: "#f59e0b" },
    { label: t("adminLabRules.columns.weeklyMinutesPerUser"),     align: "center", accent: "#6366f1" },
    { label: t("adminLabRules.columns.activeReservationsPerUser"),align: "center", accent: "#a855f7" },
    { label: t("adminLabRules.columns.actions"),                  align: "right"  },
  ];

  return (
    <Box sx={pageWrap}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
        alignItems={{ sm: "center" }} spacing={2} sx={{ mb: 3 }}>

        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <RuleIcon sx={{ fontSize: 22, color: "#6366f1" }} />
            <Typography variant="h5" fontWeight={700}>
              {t("adminLabRules.title")}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("adminLabRules.subtitle")}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Mini KPIs — même style que AdminCreditsQuotasPage */}
          {[
            { val: labs.length,  label: "labs",                                     color: "#6366f1" },
            { val: activeCount,  label: t("admin.labs_active").toLowerCase(),        color: "#22c55e" },
            { val: quotaCount,   label: t("admin.labs_with_quotas").toLowerCase(),   color: "#f59e0b" },
          ].map(({ val, label, color }) => (
            <Box key={label} sx={{ px: 1.5, py: 0.75, bgcolor: "#fff",
              border: "1px solid", borderColor: "divider", borderRadius: 2,
              textAlign: "center", minWidth: 62 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 800, color, lineHeight: 1 }}>{val}</Typography>
              <Typography sx={{ fontSize: 10, color: "text.disabled", mt: 0.25,
                textTransform: "uppercase", letterSpacing: ".4px" }}>{label}</Typography>
            </Box>
          ))}

          <Tooltip title={t("adminLabRules.refresh")}>
            <IconButton onClick={loadLabs} disabled={loading} size="small"
              sx={{ border: "1px solid", borderColor: "divider", ...r2 }}>
              {loading
                ? <CircularProgress size={16} />
                : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Barre filtres ────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider",
        borderRadius: 3, p: 2, mb: 2.5 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}
          alignItems="center" flexWrap="wrap">

          {/* Recherche */}
          <TextField size="small" placeholder={t("common.search") + "…"}
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { ...r2 } }}
            InputProps={{ startAdornment:
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            }} />

          {/* Filtre statut */}
          <FormControl size="small" sx={{ minWidth: 185 }}>
            <Select value={filter} onChange={e => setFilter(e.target.value)} sx={r2}>
              <MenuItem value="all">{t("admin.labs_all")}</MenuItem>
              <MenuItem value="active">{t("admin.labs_active")}</MenuItem>
              <MenuItem value="inactive">{t("admin.labs_inactive")}</MenuItem>
              <MenuItem value="with_quota">{t("admin.labs_with_quotas")}</MenuItem>
            </Select>
          </FormControl>

          {/* Légende couleurs colonnes */}
          <Stack direction="row" spacing={2} sx={{ ml: "auto" }} flexWrap="wrap" alignItems="center">
            {[
              { color: "#0ea5e9", label: t("adminLabRules.columns.capacity") },
              { color: "#f59e0b", label: "min/j" },
              { color: "#6366f1", label: "min/sem" },
              { color: "#a855f7", label: "max actives" },
            ].map(({ color, label }) => (
              <Stack key={label} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color }} />
                <Typography sx={{ fontSize: 11, color: "text.disabled" }}>{label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Paper>

      {/* ── Tableau ──────────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider",
        borderRadius: 3, overflow: "hidden" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>

          {/* En-têtes */}
          <Box component="thead">
            <Box component="tr" sx={{ bgcolor: "#f8fafc" }}>
              {HEADERS.map(({ label, align, accent }, i) => (
                <Box component="th" key={i}
                  sx={{ ...tableHeaderCell, p: 1.5, textAlign: align,
                    color: accent ? accent : "text.secondary" }}>
                  {label}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Corps */}
          <Box component="tbody">
            {filtered.length === 0 && (
              <Box component="tr">
                <Box component="td" colSpan={8}
                  sx={{ py: 6, textAlign: "center", color: "text.secondary", fontSize: 14 }}>
                  {loading ? t("common.loading") : t("adminLabRules.noLabs")}
                </Box>
              </Box>
            )}

            {filtered.map(lab => {
              const isSaving  = savingId === lab.id;
              const justSaved = savedIds.has(lab.id);
              const quota     = hasQuota(lab);

              return (
                <Box component="tr" key={lab.id}
                  sx={{
                    ...tableRow,
                    "& td": { p: 1.25, borderBottom: "1px solid #f1f5f9" },
                    borderLeft: `3px solid ${justSaved ? "#22c55e" : quota ? "#f59e0b66" : "transparent"}`,
                  }}>

                  {/* Nom + description */}
                  <Box component="td">
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <FiberManualRecordIcon
                        sx={{ fontSize: 9, color: lab.is_active ? "#22c55e" : "#cbd5e1" }} />
                      <Typography variant="body2" fontWeight={600}>{lab.name}</Typography>
                      {quota && (
                        <Box sx={{ px: 0.75, py: 0.1, ...r2, bgcolor: "#fffbeb",
                          border: "1px solid #fde68a", fontSize: 9, fontWeight: 800,
                          color: "#d97706", letterSpacing: ".3px" }}>Q</Box>
                      )}
                    </Stack>
                    {lab.description && (
                      <Typography variant="caption" color="text.secondary"
                        sx={{ display: "block", mt: 0.25, pl: 1.75,
                          maxWidth: 240, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {lab.description}
                      </Typography>
                    )}
                  </Box>

                  {/* Template UUID */}
                  <Box component="td">
                    {lab.template_project_id
                      ? <Tooltip title={lab.template_project_id}>
                          <Typography variant="caption"
                            sx={{ fontFamily: "monospace", color: "#6366f1", cursor: "default" }}>
                            {lab.template_project_id.substring(0, 10)}…
                          </Typography>
                        </Tooltip>
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </Box>

                  {/* Switch actif */}
                  <Box component="td" sx={{ textAlign: "center" }}>
                    <Switch size="small" checked={!!lab.is_active}
                      onChange={e => setField(lab.id, "is_active", e.target.checked)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked":            { color: "#22c55e" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#22c55e" },
                      }} />
                  </Box>

                  {/* Capacité */}
                  <Box component="td" sx={{ textAlign: "center" }}>
                    <NumInput accent="#0ea5e9"
                      value={lab.capacity ?? ""}
                      onChange={e => setField(lab.id, "capacity", e.target.value)} />
                  </Box>

                  {/* Quota journalier */}
                  <Box component="td" sx={{ textAlign: "center" }}>
                    <NumInput accent="#f59e0b"
                      value={lab.max_daily_minutes_per_user ?? ""}
                      onChange={e => setField(lab.id, "max_daily_minutes_per_user", e.target.value)} />
                  </Box>

                  {/* Quota hebdomadaire */}
                  <Box component="td" sx={{ textAlign: "center" }}>
                    <NumInput accent="#6366f1"
                      value={lab.max_weekly_minutes_per_user ?? ""}
                      onChange={e => setField(lab.id, "max_weekly_minutes_per_user", e.target.value)} />
                  </Box>

                  {/* Max réservations actives */}
                  <Box component="td" sx={{ textAlign: "center" }}>
                    <NumInput accent="#a855f7"
                      value={lab.max_active_reservations_per_user ?? ""}
                      onChange={e => setField(lab.id, "max_active_reservations_per_user", e.target.value)} />
                  </Box>

                  {/* Bouton Save — même style iconButton que AdminLabsPage */}
                  <Box component="td" sx={{ textAlign: "right" }}>
                    <Tooltip title={justSaved ? "Sauvegardé ✓" : t("adminLabRules.saveTooltip")}>
                      <span>
                        <IconButton size="small" onClick={() => handleSave(lab)} disabled={isSaving}
                          sx={{
                            ...r2,
                            color:       justSaved ? "#22c55e" : "#6366f1",
                            bgcolor:     justSaved ? "#f0fdf4" : "transparent",
                            border:      "1px solid",
                            borderColor: justSaved ? "#86efac" : "divider",
                            transition:  "all .2s",
                            "&:hover": { bgcolor: "#eef2ff", borderColor: "#6366f1" },
                          }}>
                          {isSaving
                            ? <CircularProgress size={14} />
                            : justSaved
                              ? <CheckIcon sx={{ fontSize: 15 }} />
                              : <SaveIcon  sx={{ fontSize: 15 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>

      {/* Note */}
      <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: "block" }}>
        ∞ = {t("common.unlimited")} · {t("adminLabRules.saveTooltip")}
      </Typography>

      {/* Snackbar — même pattern que AdminLabsPage */}
      {snack && (
        <Snackbar open autoHideDuration={4000} onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity={snack.sev} onClose={() => setSnack(null)} sx={r2}>
            {snack.text}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}
